import { useEffect, useMemo, useRef, useState, Fragment } from 'react';
import {
  Alert,
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Collapse,
  CircularProgress,
} from '@mui/material';
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  ArrowDropDown as ArrowDropDownIcon,
  ArrowDropUp as ArrowDropUpIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip as ChartTooltip } from 'recharts';
import axios from '../../utils/axios';
import { useNotify } from '../../hooks';
import DateRangeField from '../../components/ui/DateRangeField';
import ProductFilters, { buildFilterParams } from '../../components/ui/ProductFilters';

// Глобальные 20 с axios (для ТСД) мало для финотчёта: Ozon «по операциям» считается ~30 с.
// Потолок — proxy_read_timeout 60 с у nginx на /api/.
const FINANCE_TIMEOUT_MS = 60000;

export default function AnalyticsFinance() {
  const { error: showError } = useNotify();
  
  const formatYmd = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  
  const today = new Date();
  const minus30 = new Date(today);
  minus30.setDate(today.getDate() - 30);
  
  // State
  const [filters, setFilters] = useState({ mp: '1' });
  const [dateFrom, setDateFrom] = useState(formatYmd(minus30));
  const [dateTo, setDateTo] = useState(formatYmd(today));
  const [agg, setAgg] = useState('day');
  // order — по дате заказа; ops — по операциям; cohort_ops — заказы периода, строки по дате операций
  const [dateMode, setDateMode] = useState('order');
  const [rows, setRows] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [expandedOrders, setExpandedOrders] = useState({});
  const [expandedOps, setExpandedOps] = useState({});
  const [orderIdFilter, setOrderIdFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [zeroCostOrders, setZeroCostOrders] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const loadSeqRef = useRef(0);
  // Период загруженной таблицы: раскрытия cohort_ops берут из него диапазон заказов,
  // даже если поля дат уже поменяли без перезагрузки.
  const loadedPeriodRef = useRef({ from: null, to: null });

  const isWB = filters.mp === '1';
  const isOzon = filters.mp === '2';
  const isYandex = filters.mp === '3';
  const isPeriodic = agg !== 'day';
  const apiPrefix = isWB ? '/analytics/wb' : (isYandex ? '/analytics/ya' : '/analytics');
  const opsModeLabel = isYandex ? 'По дате смены статуса' : 'По операциям';
  const dateModeHints = {
    order: 'Заказы, оформленные в выбранный период; все их операции — в строке дня заказа.',
    ops: isYandex
      ? 'Строки по дате первой смены статуса заказа; период фильтрует эту дату.'
      : 'Все операции, случившиеся в выбранный период, по дню операции — независимо от даты заказа.',
    cohort_ops: isYandex
      ? 'Заказы, оформленные в выбранный период, разложены по дате первой смены статуса — в том числе после конца периода. Сумма по всем дням = режим «По дате заказа».'
      : 'Заказы, оформленные в выбранный период, разложены по дням их операций — в том числе после конца периода. Сумма по всем дням = режим «По дате заказа».',
  };
  const withDateMode = (params) => {
    if (dateMode !== 'order') params.date_mode = dateMode;
    if (dateMode === 'cohort_ops') {
      const { from, to } = loadedPeriodRef.current;
      if (from) params.cohort_from = from;
      if (to) params.cohort_to = to;
    }
    return params;
  };
  // Выбор периода унифицирован через общий компонент DateRangeField

  // Weekly теперь доступен для всех МП (WB/Ozon/Yandex)

  // Стили для таблиц
  const cellStyle = { py: 0.5, px: 1, fontSize: '0.8rem', whiteSpace: 'nowrap' };
  const headerStyle = { ...cellStyle, fontWeight: 'bold', bgcolor: 'grey.100' };

  // Форматирование чисел
  const fmtNum = (v, decimals = 0) => {
    if (v == null || v === '') return '-';
    const n = Number(v);
    if (isNaN(n)) return '-';
    return n.toLocaleString('ru-RU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };
  const fmtMoney = (v) => fmtNum(v, 2);
  const fmtPct = (v) => {
    if (v == null || v === '') return '-';
    return `${fmtNum(v, 2)}%`;
  };

  // Форматирование значений таблицы по ключу колонки (как в AnalyticsAdvert)
  const formatValueByKey = (key, v) => {
    if (v == null || v === '') return '-';
    if (key === 'order_date') return formatDate(v);
    if (key === 'commission_pct' || key === 'commission_pct_net' || key === 'buyout_rate_pct' || key === 'roi_pct') return fmtPct(v);
    if (key.startsWith('items_')) return fmtNum(v, 0);
    if (key === 'avg_logistics' || key === 'avg_kgt_return_cost') return fmtNum(v, 2);

    // Денежные/суммовые поля
    if (
      key.includes('_sum') ||
      key.includes('amount') ||
      key.includes('cost') ||
      key.includes('profit') ||
      key.includes('retail') ||
      key.includes('payment') ||
      key.includes('accrual') ||
      key.includes('commission') ||
      key.includes('logistics') ||
      key.includes('acquiring') ||
      key.includes('storage') ||
      key.includes('ads') ||
      key.includes('nds') ||
      key.includes('bonus') ||
      key === 'total_to_pay' ||
      key === 'total_deductions'
    ) {
      return fmtMoney(v);
    }

    // Фолбэк: пробельный разделитель тысяч
    return fmtNum(v, 0);
  };

  const load = async () => {
    const loadSeq = loadSeqRef.current + 1;
    loadSeqRef.current = loadSeq;
    setLoading(true);
    setBreakdown(null);
    setBreakdownLoading(false);
    try {
      const params = { ...buildFilterParams(filters) };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (dateMode !== 'order') params.date_mode = dateMode;
      if (orderIdFilter.trim()) params.order_id = orderIdFilter.trim();
      loadedPeriodRef.current = { from: dateFrom || null, to: dateTo || null };
      const endpoint = (agg === 'day') ? 'daily' : 'weekly';
      if (agg !== 'day' && agg !== 'week') params.agg = agg;
      const [resp, zcResp] = await Promise.all([
        axios.get(`${apiPrefix}/${endpoint}`, { params, timeout: FINANCE_TIMEOUT_MS }),
        axios.get('/analytics/zero-cost-check', {
          params: { mp: filters.mp || '', date_from: dateFrom, date_to: dateTo },
        }).catch(() => ({ data: { rows: [] } })),
      ]);
      setRows(resp.data.rows || []);
      setZeroCostOrders(zcResp.data.rows || []);
      setExpanded({});
      setExpandedOrders({});
      setExpandedOps({});

      if ((resp.data.rows || []).length > 0) {
        setBreakdownLoading(true);
        axios.get(`${apiPrefix}/${endpoint}`, { params: { ...params, include_breakdown: '1' }, timeout: FINANCE_TIMEOUT_MS })
          .then((breakdownResp) => {
            if (loadSeqRef.current !== loadSeq) return;
            setBreakdown(breakdownResp.data.breakdown || null);
          })
          .catch((e) => {
            if (loadSeqRef.current !== loadSeq) return;
            showError(e?.response?.data?.message || e?.message || 'Ошибка загрузки диаграмм');
          })
          .finally(() => {
            if (loadSeqRef.current !== loadSeq) return;
            setBreakdownLoading(false);
          });
      }
    } catch (e) {
      showError(e?.response?.data?.message || e?.message || 'Ошибка загрузки данных');
      setBreakdown(null);
      setBreakdownLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setRows([]);
    setBreakdown(null);
    setBreakdownLoading(false);
    setZeroCostOrders([]);
    setExpanded({});
    setExpandedOrders({});
    setExpandedOps({});
  }, [filters.mp]);

  useEffect(() => {
    setRows([]);
    setBreakdown(null);
    setBreakdownLoading(false);
    setExpanded({});
    setExpandedOrders({});
    setExpandedOps({});
  }, [agg]);

  useEffect(() => {
    setRows([]);
    setBreakdown(null);
    setBreakdownLoading(false);
    setExpanded({});
    setExpandedOrders({});
    setExpandedOps({});
  }, [dateMode]);

  const columnTooltips = useMemo(() => {
    const opsDate = isYandex ? 'дата первой смены статуса заказа' : 'дата операции';
    const orderDateTip = {
      order: 'Дата заказа или период',
      ops: `Дата или период: ${opsDate}`,
      cohort_ops: `Дата или период: ${opsDate}. Строки — только заказы, оформленные в выбранный период`,
    }[dateMode];
    if (isWB) return {
      order_date: orderDateTip,
      items_ordered: 'Количество отправок товаров к покупателям за период',
      items_buyout: 'Количество товаров, которые покупатели оплатили и забрали',
      items_return: 'Количество товаров, которые покупатели вернули',
      items_defect: 'Количество товаров, признанных бракованными или неопознанными',
      items_kgt: 'Количество крупногабаритных товаров, возвращённых продавцу',
      buyout_rate_pct: '(Выкуплено − Возврат − Брак) ÷ Заказано × 100%',
      avg_order_price: 'Продажи (розн) ÷ Заказано',
      retail_price_net_sum: 'Сумма продаж по полной розничной цене за вычетом возвратов (до скидки покупателя)',
      commission_sum: 'Удержание маркетплейса: Продажи (розн) − К перечислению (с учётом возвратов)',
      acquiring_sum: 'Удержание за обработку онлайн-платежей. Уже входит в расчёт «К перечислению»',
      retail_amount_net_sum: 'Сумма продаж по цене, которую реально заплатил покупатель (со скидкой постоянного покупателя), за вычетом возвратов',
      ppvz_for_pay_sum: 'Начисления от WB продавцу: выручка с продаж за вычетом комиссии, скидок и возвратов',
      logistics_sum: 'Расходы на доставку товаров покупателям и обратно (включая сторно)',
      avg_logistics: '|Логистика доставок к покупателям| ÷ Заказано',
      storage_sum: 'Расходы на хранение товаров на складе Wildberries',
      ads_sum: 'Расходы на рекламу: программа лояльности + WB Продвижение',
      other_sum: 'Штрафы + платная приёмка + прочие удержания (включая списание платежа по факторингу) − компенсации от WB',
      total_to_pay: 'К перечислению + Логистика + Хранение + Реклама + Прочие — сколько WB фактически перечислит',
      avg_kgt_return_cost: 'Общая стоимость возврата КГТ ÷ Количество возвратов КГТ',
      nds_out_sum: 'С 01.09.2026 (ДомДизайн): Продажи (СПП) × 22/122, за вычетом возвратов. До этого и для других организаций — НДС по ставке кабинета',
      nds_in_mp_sum: 'НДС с вознаграждения WB (из отчёта, бывает отрицательным, когда WB доплачивает за СПП больше комиссии) + (логистика + хранение + приёмка + WB Продвижение + Джем) × 22/122',
      nds_in_prod_sum: 'Себестоимость × «Исходящий НДС» карточки ÷ 100 (пусто → 13%)',
      nds_sum: 'НДС с продаж − к вычету WB − к вычету производство',
      cost_sum: 'Себестоимость проданных товаров за вычетом возвратов (из карточки товара)',
      profit: 'Итого к оплате − Себестоимость − НДС к уплате. Списание платежа по факторингу в профит НЕ входит: это возврат финансирования, а не расход бизнеса',
      roi_pct: 'Профит ÷ Себестоимость × 100%',
    };
    if (isYandex) return {
      order_date: orderDateTip,
      items_ordered: 'Количество оформленных заказов за период',
      avg_order_price: 'По нашей цене ÷ Заказано',
      items_sold: 'Количество товаров со статусом «Доставлен»',
      items_returned: 'Количество товаров со статусом «Невыкуп» или «Возврат»',
      items_cancelled: 'Количество заказов, отменённых при доставке',
      retail_sum: 'Сумма продаж по розничной цене (только доставленные товары)',
      payment_sum: 'Сумма, которую фактически заплатил покупатель с учётом всех скидок (только доставленные)',
      commission_sum: 'Удержанная комиссия Яндекс Маркета с продаж',
      ppvz_for_pay_sum: 'Доход за вычетом услуг маркетплейса плюс списание бонусов (только доставленные). Списание в профит не входит',
      bonus_accrual_sum: 'Бонусы Маркета, начисленные покупателю. Вычитаются из базы профита и из базы НДС: цена − начисление',
      bonus_writeoff_sum: 'Списание бонусов — периодическая проводка с лагом, не привязанная к заказу. В профит не входит; учтено в «Доход - маркет»',
      logistics_sum: 'Расходы на доставку товаров покупателям (все статусы)',
      acquiring_sum: 'Удержание за обработку онлайн-платежей',
      storage_sum: 'Расходы на хранение товаров на складе маркетплейса',
      ads_sum: 'Расходы на продвижение из собственных средств: буст продаж + полки',
      ads_bonus_sum: 'Реклама, оплаченная промо-бонусами Яндекс Маркета',
      other_sum: 'Обработка заказа + работа склада + программа лояльности + рассрочка',
      total_deductions: 'Сумма всех удержаний: комиссия + логистика + эквайринг + реклама + хранение + прочие',
      total_to_pay: 'Сумма платежа − Всего удержано',
      nds_out_sum: 'С 01.09.2026 (ДомДизайн, по дате заказа): (По нашей цене − Бонус +) × 22/122 — бонусы за совместные акции Маркет даёт скидкой на размещение, а не деньгами. До этого — НДС по ставке кабинета',
      nds_in_mp_sum: '(Комиссия, оплаченная деньгами + логистика + буст + хранение + лояльность + полки и др. аккаунтные услуги) × 22/122. Эквайринг без НДС',
      nds_in_prod_sum: 'Себестоимость × «Исходящий НДС» карточки ÷ 100 (пусто → 13%)',
      nds5_sum: 'НДС с продаж − к вычету Маркет − к вычету производство. До 01.09.2026 и для других организаций — НДС по ставке кабинета',
      cost_sum: 'Себестоимость доставленных товаров (цена из карточки × кол-во)',
      profit: 'Доход за вычетом услуг − начисленные бонусы − НДС к уплате − себестоимость − расходы невыкупа и аккаунтные услуги. Списание бонусов не входит',
      roi_pct: 'Профит ÷ Себестоимость × 100%',
    };
    return {
      order_date: orderDateTip,
      items_ordered_total: 'Общее количество оформленных заказов за период',
      avg_order_price: 'Сумма продаж ÷ Заказано',
      items_cancelled_immediately: 'Количество заказов, отменённых покупателем до отправки',
      items_buyout: 'Количество товаров, которые покупатели получили и оплатили',
      buyout_rate_pct: 'Выкуплено ÷ (Заказано − Отменено) × 100%',
      accruals_for_sale_sum: 'Начисления Ozon продавцу за проданные товары (до вычета комиссии)',
      commission_sum: 'Удержанная комиссия Ozon с продаж',
      nds_out_sum: 'С 01.09.2026 (ДомДизайн): Сумма продаж × 22/122, за вычетом возвратов. До этого — НДС по ставке кабинета',
      nds_in_mp_sum: '(Комиссия + услуги Ozon: логистика, реклама, хранение, досрочная выплата и др.) × 22/122. Без НДС: эквайринг, штрафы, страхование, компенсации',
      nds_in_prod_sum: 'Себестоимость × «Исходящий НДС» карточки ÷ 100 (пусто → 13%)',
      nds_sum: 'НДС с продаж − к вычету Ozon − к вычету производство',
      amount_sum: 'Сумма начислений за вычетом комиссии и НДС — итого к перечислению продавцу',
      logistics_cost_sum: 'Расходы на доставку товаров покупателям (последняя миля, магистраль)',
      acquiring_cost_sum: 'Удержание за обработку онлайн-платежей',
      other_cost_sum: 'Прочие удержания: обработка заказа, возвраты, подписка, штрафы',
      cost_sum: 'Себестоимость проданных товаров (из карточки товара)',
      profit: 'Сумма продаж − Комиссии − Логистика − Эквайринг − Прочие − Себестоимость − НДС к уплате',
      roi_pct: 'Профит ÷ Себестоимость × 100%',
    };
  }, [isWB, isYandex, dateMode]);

  // Headers based on MP
  const headers = useMemo(() => {
    if (isWB) {
      const wbAlign = (key) => {
        if (!isPeriodic) return 'right';
        // В недельном режиме для WB: количества/проценты/средние/ROI — по центру, суммы — вправо
        if (
          key.startsWith('items_') ||
          key === 'buyout_rate_pct' ||
          key === 'avg_logistics' ||
          key === 'avg_kgt_return_cost' ||
          key === 'roi_pct'
        ) return 'center';
        return 'right';
      };
      return [
        { key: 'order_date', label: isPeriodic ? (agg === 'quarter' ? 'Квартал' : agg === 'month' ? 'Месяц' : 'Неделя') : 'Дата' },
        { key: 'items_ordered', label: 'Заказано', align: wbAlign('items_ordered') },
        { key: 'items_buyout', label: 'Выкуплено', align: wbAlign('items_buyout') },
        { key: 'items_return', label: 'Возврат', align: wbAlign('items_return') },
        { key: 'items_defect', label: 'Брак', align: wbAlign('items_defect') },
        { key: 'items_kgt', label: 'КГТ', align: wbAlign('items_kgt') },
        { key: 'buyout_rate_pct', label: '% выкупа', align: wbAlign('buyout_rate_pct') },
        { key: 'avg_order_price', label: 'Ср. цена', align: 'right' },
        { key: 'retail_price_net_sum', label: 'Продажи (розн)', align: 'right' },
        { key: 'commission_sum', label: 'Коммиссия', align: 'right' },
        { key: 'acquiring_sum', label: 'Эквайринг', align: 'right' },
        { key: 'retail_amount_net_sum', label: 'Продажи (СПП)', align: 'right' },
        { key: 'ppvz_for_pay_sum', label: 'К перечисл.', align: 'right' },
        { key: 'logistics_sum', label: 'Логистика', align: 'right' },
        { key: 'avg_logistics', label: 'Ср. лог.', align: wbAlign('avg_logistics') },
        { key: 'storage_sum', label: 'Хранение', align: 'right' },
        { key: 'ads_sum', label: 'Реклама', align: 'right' },
        { key: 'other_sum', label: 'Прочие', align: 'right' },
        { key: 'total_to_pay', label: 'Итого к оплате', align: 'right' },
        { key: 'avg_kgt_return_cost', label: 'Ср. КГТ', align: wbAlign('avg_kgt_return_cost') },
        { key: 'nds_out_sum', label: 'НДС с продаж', align: 'right' },
        { key: 'nds_in_mp_sum', label: 'НДС к выч. WB', align: 'right' },
        { key: 'nds_in_prod_sum', label: 'НДС к выч. произв.', align: 'right' },
        { key: 'nds_sum', label: 'НДС к уплате', align: 'right' },
        { key: 'cost_sum', label: 'Себестоим.', align: 'right' },
        { key: 'profit', label: 'Профит', align: 'right' },
        { key: 'roi_pct', label: 'ROI %', align: wbAlign('roi_pct') },
      ];
    }
    if (isYandex) {
      const yaAlign = (key) => {
        if (!isPeriodic) return 'right';
        if (
          key.startsWith('items_') ||
          key === 'roi_pct'
        ) return 'center';
        return 'right';
      };
      return [
        { key: 'order_date', label: isPeriodic ? (agg === 'quarter' ? 'Квартал' : agg === 'month' ? 'Месяц' : 'Неделя') : 'Дата' },
        { key: 'items_ordered', label: 'Заказано', align: yaAlign('items_ordered') },
        { key: 'items_sold', label: 'Выкуплено', align: yaAlign('items_sold') },
        { key: 'items_returned', label: 'Возврат', align: yaAlign('items_returned') },
        { key: 'items_cancelled', label: 'Отменено', align: yaAlign('items_cancelled') },
        { key: 'avg_order_price', label: 'Ср. цена', align: 'right' },
        { key: 'retail_sum', label: 'По нашей цене', align: 'right' },
        { key: 'payment_sum', label: 'Сумма платежа', align: 'right' },
        { key: 'commission_sum', label: 'Комиссия', align: 'right' },
        { key: 'ppvz_for_pay_sum', label: 'Доход - маркет', align: 'right' },
        { key: 'bonus_accrual_sum', label: 'Бонусы начисл.', align: 'right' },
        { key: 'bonus_writeoff_sum', label: 'Бонус −', align: 'right' },
        { key: 'ads_bonus_sum', label: 'Рекл. бонусы', align: 'right' },
        { key: 'ads_sum', label: 'Реклама', align: 'right' },
        { key: 'logistics_sum', label: 'Логистика', align: 'right' },
        { key: 'acquiring_sum', label: 'Эквайринг', align: 'right' },
        { key: 'storage_sum', label: 'Хранение', align: 'right' },
        { key: 'other_sum', label: 'Прочие', align: 'right' },
        { key: 'total_deductions', label: 'Всего удержано', align: 'right' },
        { key: 'total_to_pay', label: 'Итого к оплате', align: 'right' },
        { key: 'nds_out_sum', label: 'НДС с продаж', align: 'right' },
        { key: 'nds_in_mp_sum', label: 'НДС к выч. ЯМ', align: 'right' },
        { key: 'nds_in_prod_sum', label: 'НДС к выч. произв.', align: 'right' },
        { key: 'nds5_sum', label: 'НДС к уплате', align: 'right' },
        { key: 'cost_sum', label: 'Себестоим.', align: 'right' },
        { key: 'profit', label: 'Профит', align: 'right' },
        { key: 'roi_pct', label: 'ROI %', align: yaAlign('roi_pct') },
      ];
    }
    // Ozon
    const ozAlign = (key) => {
      if (!isPeriodic) return 'right';
      if (key.startsWith('items_') || key === 'buyout_rate_pct' || key === 'roi_pct') return 'center';
      return 'right';
    };
    return [
      { key: 'order_date', label: isPeriodic ? (agg === 'quarter' ? 'Квартал' : agg === 'month' ? 'Месяц' : 'Неделя') : 'Дата' },
      { key: 'items_ordered_total', label: 'Заказано', align: ozAlign('items_ordered_total') },
      { key: 'items_cancelled_immediately', label: 'Отменено', align: ozAlign('items_cancelled_immediately') },
      { key: 'items_buyout', label: 'Выкуплено', align: ozAlign('items_buyout') },
      { key: 'buyout_rate_pct', label: '% выкупа', align: ozAlign('buyout_rate_pct') },
      { key: 'avg_order_price', label: 'Ср. цена', align: 'right' },
      { key: 'accruals_for_sale_sum', label: 'Сумма продаж', align: 'right' },
      { key: 'commission_sum', label: 'Комиссии', align: 'right' },
      { key: 'nds_out_sum', label: 'НДС с продаж', align: 'right' },
      { key: 'nds_in_mp_sum', label: 'НДС к выч. Ozon', align: 'right' },
      { key: 'nds_in_prod_sum', label: 'НДС к выч. произв.', align: 'right' },
      { key: 'nds_sum', label: 'НДС к уплате', align: 'right' },
      { key: 'amount_sum', label: 'К перечисл.', align: 'right' },
      { key: 'logistics_cost_sum', label: 'Логистика', align: 'right' },
      { key: 'acquiring_cost_sum', label: 'Эквайринг', align: 'right' },
      { key: 'other_cost_sum', label: 'Прочие', align: 'right' },
      { key: 'cost_sum', label: 'Себестоим.', align: 'right' },
      { key: 'profit', label: 'Профит', align: 'right' },
      { key: 'roi_pct', label: 'ROI %', align: ozAlign('roi_pct') },
    ];
  }, [isWB, isYandex, isPeriodic]);

  // Totals
  const totals = useMemo(() => {
    const num = (v) => Number(v || 0);
    const numKey = (row, key) => {
      const v = row?.[key];
      if (v && typeof v === 'object' && 'value' in v) return Number(v.value || 0);
      return Number(v || 0);
    };
    if (isWB) {
      const s = rows.reduce((acc, r) => {
        acc.ordered += numKey(r, 'items_ordered');
        acc.buyout += numKey(r, 'items_buyout');
        acc.returns += numKey(r, 'items_return');
        acc.defect += numKey(r, 'items_defect');
        acc.kgt += numKey(r, 'items_kgt');
        acc.amountNet += numKey(r, 'retail_amount_net_sum');
        acc.priceNet += numKey(r, 'retail_price_net_sum');
        acc.commNet += numKey(r, 'commission_sum');
        acc.nds += numKey(r, 'nds_sum');
        acc.ndsOut += numKey(r, 'nds_out_sum');
        acc.ndsInMp += numKey(r, 'nds_in_mp_sum');
        acc.ndsInProd += numKey(r, 'nds_in_prod_sum');
        acc.ppvz += numKey(r, 'ppvz_for_pay_sum');
        acc.logi += numKey(r, 'logistics_sum');
        acc.acq += numKey(r, 'acquiring_sum');
        acc.storage += numKey(r, 'storage_sum');
        acc.ads += numKey(r, 'ads_sum');
        acc.other += numKey(r, 'other_sum');
        acc.cost += numKey(r, 'cost_sum');
        acc.profit += numKey(r, 'profit');
        return acc;
      }, { ordered: 0, buyout: 0, returns: 0, defect: 0, kgt: 0, amountNet: 0, priceNet: 0, commNet: 0, nds: 0, ndsOut: 0, ndsInMp: 0, ndsInProd: 0, ppvz: 0, logi: 0, acq: 0, storage: 0, ads: 0, other: 0, cost: 0, profit: 0 });
      const denom = s.ordered || 0;
      const buyoutRate = denom ? +(100 * (s.buyout - s.returns - s.defect) / denom).toFixed(2) : 0;
      const roi = s.cost ? +(100 * s.profit / s.cost).toFixed(2) : 0;
      const avgLogi = s.ordered ? +(Math.abs(s.logi) / s.ordered).toFixed(2) : 0;
      const commPctNet = s.priceNet ? +(100 * (1 - (s.ppvz / s.priceNet))).toFixed(2) : 0;
      return {
        ordered: s.ordered, buyout: s.buyout, returns: s.returns, defect: s.defect, kgt: s.kgt, buyoutRate,
        amountNet: +s.amountNet.toFixed(2),
        priceNet: +s.priceNet.toFixed(2),
        commNet: +s.commNet.toFixed(2),
        commPctNet,
        nds: +s.nds.toFixed(2), ndsOut: +s.ndsOut.toFixed(2), ndsInMp: +s.ndsInMp.toFixed(2), ndsInProd: +s.ndsInProd.toFixed(2),
        ppvz: +s.ppvz.toFixed(2), logi: +s.logi.toFixed(2), avgLogi, acq: +s.acq.toFixed(2),
        storage: +s.storage.toFixed(2), ads: +s.ads.toFixed(2), other: +s.other.toFixed(2),
        totalToPay: +(s.ppvz + s.logi + s.ads + s.other + s.storage).toFixed(2),
        cost: +s.cost.toFixed(2), profit: +s.profit.toFixed(2), roi
      };
    }
    if (isYandex) {
      const s = rows.reduce((acc, r) => {
        acc.ordered += numKey(r, 'items_ordered');
        acc.sold += numKey(r, 'items_sold');
        acc.returned += numKey(r, 'items_returned');
        acc.cancelled += numKey(r, 'items_cancelled');
        acc.retail += numKey(r, 'retail_sum');
        acc.payment += numKey(r, 'payment_sum');
        acc.comm += numKey(r, 'commission_sum');
        acc.ppvz += numKey(r, 'ppvz_for_pay_sum');
        acc.logi += numKey(r, 'logistics_sum');
        acc.acq += numKey(r, 'acquiring_sum');
        acc.storage += numKey(r, 'storage_sum');
        acc.ads += numKey(r, 'ads_sum');
        acc.adsBonus += numKey(r, 'ads_bonus_sum');
        acc.other += numKey(r, 'other_sum');
        acc.bonusAccrual += numKey(r, 'bonus_accrual_sum');
        acc.bonusWriteoff += numKey(r, 'bonus_writeoff_sum');
        acc.nds5 += numKey(r, 'nds5_sum');
        acc.ndsOut += numKey(r, 'nds_out_sum');
        acc.ndsInMp += numKey(r, 'nds_in_mp_sum');
        acc.ndsInProd += numKey(r, 'nds_in_prod_sum');
        acc.cost += numKey(r, 'cost_sum');
        acc.profit += numKey(r, 'profit');
        return acc;
      }, { ordered: 0, sold: 0, returned: 0, cancelled: 0, retail: 0, payment: 0, comm: 0, ppvz: 0, logi: 0, acq: 0, storage: 0, ads: 0, adsBonus: 0, other: 0, bonusAccrual: 0, bonusWriteoff: 0, nds5: 0, ndsOut: 0, ndsInMp: 0, ndsInProd: 0, cost: 0, profit: 0 });
      const commNeg = -Math.abs(s.comm);
      const totalDed = +(commNeg - s.logi + s.acq + s.ads + s.adsBonus + s.storage + s.other).toFixed(2);
      const totalToPay = +(s.payment + totalDed).toFixed(2);
      const profit = +s.profit.toFixed(2);
      const roi = s.cost ? +(100 * profit / s.cost).toFixed(2) : 0;
      return { ordered: s.ordered, sold: s.sold, returned: s.returned, cancelled: s.cancelled, retail: +s.retail.toFixed(2), payment: +s.payment.toFixed(2), comm: +commNeg.toFixed(2), ppvz: +s.ppvz.toFixed(2), logi: +s.logi.toFixed(2), acq: +s.acq.toFixed(2), storage: +s.storage.toFixed(2), ads: +s.ads.toFixed(2), adsBonus: +s.adsBonus.toFixed(2), other: +s.other.toFixed(2), totalDed, totalToPay, bonusAccrual: +s.bonusAccrual.toFixed(2), bonusWriteoff: +s.bonusWriteoff.toFixed(2), nds5: +s.nds5.toFixed(2), ndsOut: +s.ndsOut.toFixed(2), ndsInMp: +s.ndsInMp.toFixed(2), ndsInProd: +s.ndsInProd.toFixed(2), cost: +s.cost.toFixed(2), profit, roi };
    }
    // Ozon
    const s = rows.reduce((acc, r) => {
      acc.ordered += numKey(r, 'items_ordered_total');
      acc.cancelled += numKey(r, 'items_cancelled_immediately');
      acc.buyout += numKey(r, 'items_buyout');
      acc.accruals += numKey(r, 'accruals_for_sale_sum');
      acc.comm += numKey(r, 'commission_sum');
      acc.nds += numKey(r, 'nds_sum');
      acc.ndsOut += numKey(r, 'nds_out_sum');
      acc.ndsInMp += numKey(r, 'nds_in_mp_sum');
      acc.ndsInProd += numKey(r, 'nds_in_prod_sum');
      acc.amount += numKey(r, 'amount_sum');
      acc.logi += numKey(r, 'logistics_cost_sum');
      acc.acq += numKey(r, 'acquiring_cost_sum');
      acc.other += numKey(r, 'other_cost_sum');
      acc.cost += numKey(r, 'cost_sum');
      acc.profit += numKey(r, 'profit');
      return acc;
    }, { ordered: 0, cancelled: 0, buyout: 0, accruals: 0, comm: 0, nds: 0, ndsOut: 0, ndsInMp: 0, ndsInProd: 0, amount: 0, logi: 0, acq: 0, other: 0, cost: 0, profit: 0 });
    const denom = (s.ordered - s.cancelled) || 0;
    const buyoutRate = denom ? +(100 * s.buyout / denom).toFixed(2) : 0;
    const roi = s.cost ? +(100 * s.profit / s.cost).toFixed(2) : 0;
    return { ordered: s.ordered, cancelled: s.cancelled, buyout: s.buyout, buyoutRate, accruals: +s.accruals.toFixed(2), comm: +s.comm.toFixed(2), nds: +s.nds.toFixed(2), ndsOut: +s.ndsOut.toFixed(2), ndsInMp: +s.ndsInMp.toFixed(2), ndsInProd: +s.ndsInProd.toFixed(2), amount: +s.amount.toFixed(2), logi: +s.logi.toFixed(2), acq: +s.acq.toFixed(2), other: +s.other.toFixed(2), cost: +s.cost.toFixed(2), profit: +s.profit.toFixed(2), roi };
  }, [rows, isWB, isYandex, isOzon]);

  const formatDate = (d) => d ? String(d).split('T')[0] : '';

  const numOrNull = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const isLowerBetterKey = (key) => {
    // Метрики, где рост значения = ухудшение (меньше — лучше)
    return (
      key === 'items_return' ||
      key === 'items_defect' ||
      key === 'items_kgt' ||
      key === 'items_returned' ||
      key === 'items_cancelled' ||
      key === 'items_cancelled_immediately' ||
      key === 'avg_logistics' ||
      key === 'avg_kgt_return_cost' ||
      key === 'commission_pct' ||
      key === 'commission_pct_net'
    );
  };

  const isExpenseSumKey = (key) => {
    // Денежные расходы: оцениваем "хорошо" как уменьшение по модулю (работает и для отрицательных сумм)
    return (
      key === 'commission_sum' ||
      key === 'acquiring_sum' ||
      key === 'logistics_sum' ||
      key === 'storage_sum' ||
      key === 'ads_sum' ||
      key === 'ads_bonus_sum' ||
      key === 'other_sum' ||
      key === 'total_deductions' ||
      key === 'nds_sum' ||
      key === 'nds_out_sum' ||
      key === 'nds5_sum' ||
      key === 'logistics_cost_sum' ||
      key === 'acquiring_cost_sum' ||
      key === 'other_cost_sum' ||
      key === 'cost_sum'
    );
  };

  const calcChangePct = (curr, prev) => {
    if (!Number.isFinite(curr) || !Number.isFinite(prev)) return null;
    if (prev === 0) {
      if (curr === 0) return 0;
      return null; // чтобы не показывать бесконечность
    }
    return 100 * ((curr - prev) / Math.abs(prev));
  };

  const renderShare = (metricObj, key) => {
    if (!metricObj || typeof metricObj !== 'object') return null;
    const sharePct = metricObj.share_pct;
    const deltaPp = metricObj.share_delta_pp;
    // Если доли нет — показываем delta_pct (процент изменения значения)
    if (sharePct == null) {
      const dp = Number(metricObj.delta_pct);
      if (!Number.isFinite(dp) || dp === 0) return null;
      const ArrowIcon = dp > 0 ? ArrowDropUpIcon : ArrowDropDownIcon;
      const lowerBetter = isLowerBetterKey(key);
      const good = isExpenseSumKey(key) ? (dp < 0) : (lowerBetter ? (dp < 0) : (dp > 0));
      const color = good ? 'success.main' : 'error.main';
      return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
          <ArrowIcon fontSize="small" sx={{ color, lineHeight: 1, mx: -0.5 }} />
          <Typography variant="caption" sx={{ color, lineHeight: 1 }}>
            {fmtNum(Math.abs(dp), 2)}%
          </Typography>
        </Box>
      );
    }

    const delta = Number(deltaPp);
    const hasDelta = Number.isFinite(delta);

    let ArrowIcon = null;
    let deltaText = '';
    let color = 'text.secondary';

    if (hasDelta && delta !== 0) {
      ArrowIcon = delta > 0 ? ArrowDropUpIcon : ArrowDropDownIcon;
      deltaText = `${fmtNum(Math.abs(delta), 2)}`;

      const lowerBetter = isLowerBetterKey(key);
      const good = isExpenseSumKey(key) ? (delta < 0) : (lowerBetter ? (delta < 0) : (delta > 0));
      color = good ? 'success.main' : 'error.main';
    }

    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color }}>
        <Typography variant="caption" sx={{ color, lineHeight: 1 }}>
          {fmtNum(sharePct, 2)}%
        </Typography>
        {hasDelta && delta !== 0 && ArrowIcon ? <ArrowIcon fontSize="small" sx={{ color, lineHeight: 1, mx: -0.5 }} /> : null}
        {hasDelta && delta !== 0 ? <Typography variant="caption" sx={{ color, lineHeight: 1 }}>{deltaText}</Typography> : null}
      </Box>
    );
  };

  const getMetricValue = (row, key) => {
    if (key === 'total_to_pay' && isWB) {
      const n = (k) => { const val = row?.[k]; return val && typeof val === 'object' && 'value' in val ? Number(val.value || 0) : Number(val || 0); };
      return +(n('ppvz_for_pay_sum') + n('logistics_sum') + n('ads_sum') + n('other_sum') + n('storage_sum')).toFixed(2);
    }
    if (key === 'commission_sum' && isYandex) {
      const n = (k) => { const val = row?.[k]; return val && typeof val === 'object' && 'value' in val ? Number(val.value || 0) : Number(val || 0); };
      return -Math.abs(n('commission_sum'));
    }
    if (key === 'total_deductions' && isYandex) {
      const n = (k) => { const val = row?.[k]; return val && typeof val === 'object' && 'value' in val ? Number(val.value || 0) : Number(val || 0); };
      return +(-Math.abs(n('commission_sum')) - n('logistics_sum') + n('acquiring_sum') + n('ads_sum') + n('ads_bonus_sum') + n('storage_sum') + n('other_sum')).toFixed(2);
    }
    if (key === 'total_to_pay' && isYandex) {
      const n = (k) => { const val = row?.[k]; return val && typeof val === 'object' && 'value' in val ? Number(val.value || 0) : Number(val || 0); };
      const totalDed = -Math.abs(n('commission_sum')) - n('logistics_sum') + n('acquiring_sum') + n('ads_sum') + n('ads_bonus_sum') + n('storage_sum') + n('other_sum');
      return +(n('payment_sum') + totalDed).toFixed(2);
    }
    const v = row?.[key];
    if (v && typeof v === 'object' && 'value' in v) return v.value;
    return v;
  };

  const getCellValue = (row, key) => {
    if (isPeriodic && key === 'order_date') {
      const a = row?.week_start ? String(row.week_start) : '';
      const b = row?.week_end ? String(row.week_end) : '';
      return (a && b) ? `${a}–${b}` : (a || b || '-');
    }
    return formatValueByKey(key, getMetricValue(row, key));
  };

  const getTotalValue = (key) => {
    if (isWB) {
      const map = {
        items_ordered: totals.ordered, items_buyout: totals.buyout, items_return: totals.returns, items_defect: totals.defect, items_kgt: totals.kgt,
        buyout_rate_pct: totals.buyoutRate,
        retail_price_net_sum: totals.priceNet,
        commission_sum: totals.commNet,
        commission_pct_net: totals.commPctNet,
        retail_amount_net_sum: totals.amountNet,
        nds_sum: totals.nds, nds_out_sum: totals.ndsOut, nds_in_mp_sum: totals.ndsInMp, nds_in_prod_sum: totals.ndsInProd,
        ppvz_for_pay_sum: totals.ppvz, logistics_sum: totals.logi, avg_logistics: totals.avgLogi, acquiring_sum: totals.acq,
        storage_sum: totals.storage, ads_sum: totals.ads, other_sum: totals.other,
        total_to_pay: totals.totalToPay, avg_kgt_return_cost: '—', cost_sum: totals.cost,
        profit: totals.profit, roi_pct: totals.roi
      };
      return map[key] ?? '';
    }
    if (isYandex) {
      const map = { items_ordered: totals.ordered, items_sold: totals.sold, items_returned: totals.returned, items_cancelled: totals.cancelled, retail_sum: totals.retail, payment_sum: totals.payment, commission_sum: totals.comm, ppvz_for_pay_sum: totals.ppvz, bonus_accrual_sum: totals.bonusAccrual, bonus_writeoff_sum: totals.bonusWriteoff, logistics_sum: totals.logi, acquiring_sum: totals.acq, storage_sum: totals.storage, ads_sum: totals.ads, ads_bonus_sum: totals.adsBonus, other_sum: totals.other, total_deductions: totals.totalDed, total_to_pay: totals.totalToPay, nds_out_sum: totals.ndsOut, nds_in_mp_sum: totals.ndsInMp, nds_in_prod_sum: totals.ndsInProd, nds5_sum: totals.nds5, cost_sum: totals.cost, profit: totals.profit, roi_pct: totals.roi };
      return map[key] ?? '';
    }
    const map = { items_ordered_total: totals.ordered, items_cancelled_immediately: totals.cancelled, items_buyout: totals.buyout, buyout_rate_pct: totals.buyoutRate, accruals_for_sale_sum: totals.accruals, commission_sum: totals.comm, nds_sum: totals.nds, nds_out_sum: totals.ndsOut, nds_in_mp_sum: totals.ndsInMp, nds_in_prod_sum: totals.ndsInProd, amount_sum: totals.amount, logistics_cost_sum: totals.logi, acquiring_cost_sum: totals.acq, other_cost_sum: totals.other, cost_sum: totals.cost, profit: totals.profit, roi_pct: totals.roi };
    return map[key] ?? '';
  };

  // Group headers
  const getGroupHeaders = () => {
    if (isWB) return ['Группа', 'Подгруппа', 'Заказано', 'Выкуплено', 'Возврат', 'Брак', 'КГТ', '% выкупа', 'Продажи (розн)', 'Коммиссия', 'Коммиссия %', 'Эквайринг', 'Продажи (СПП)', 'К перечисл.', 'Логистика', 'Ср. лог.', 'Хранение', 'Реклама', 'Прочие', 'Итого к оплате', 'Ср. КГТ', 'НДС с продаж', 'НДС к выч. WB', 'НДС к выч. произв.', 'НДС к уплате', 'Себестоим.', 'Профит', 'ROI %'];
    if (isYandex) return ['Группа', 'Подгруппа', 'Заказано', 'Выкуплено', 'Возврат', 'Отменено', 'По нашей цене', 'Сумма платежа', 'Комиссия', 'Доход - маркет', 'Бонусы начисл.', 'Бонус −', 'Рекл. бонусы', 'Реклама', 'Логистика', 'Эквайринг', 'Хранение', 'Прочие', 'Всего удержано', 'Итого к оплате', 'НДС с продаж', 'НДС к выч. ЯМ', 'НДС к выч. произв.', 'НДС к уплате', 'Себестоим.', 'Профит', 'ROI %'];
    return ['Группа', 'Подгруппа', 'Заказано', 'Отменено', 'Выкуплено', '% выкупа', 'Сумма продаж', 'Комиссии', 'НДС с продаж', 'НДС к выч. Ozon', 'НДС к выч. произв.', 'НДС к уплате', 'К перечисл.', 'Логистика', 'Эквайринг', 'Прочие', 'Себестоим.', 'Профит', 'ROI %'];
  };

  // Toggle row expand
  const toggleDate = async (dateKey) => {
    if (expanded[dateKey]) {
      setExpanded(prev => { const c = { ...prev }; delete c[dateKey]; return c; });
      return;
    }
    const fp = buildFilterParams(filters);
    const params = { date: dateKey };
    if (fp.mp) params.mp = fp.mp;
    if (fp.organization) params.organization = fp.organization;
    if (fp.organization_exclude) params.organization_exclude = fp.organization_exclude;
    withDateMode(params);
    if (orderIdFilter.trim()) params.order_id = orderIdFilter.trim();
    const resp = await axios.get(`${apiPrefix}/daily-by-group`, { params, timeout: FINANCE_TIMEOUT_MS });
    setExpanded(prev => ({ ...prev, [dateKey]: resp.data.rows || [] }));
  };

  const toggleGroup = async (dateKey, g) => {
    const k = `${dateKey}|${g.gruppa}|${g.podgruppa || ''}`;
    if (expandedOrders[k]) {
      setExpandedOrders(prev => { const c = { ...prev }; delete c[k]; return c; });
      return;
    }
    const fp = buildFilterParams(filters);
    const params = withDateMode({ date: dateKey.split('T')[0], gruppa: g.gruppa || '', podgruppa: g.podgruppa || '' });
    if (fp.mp) params.mp = fp.mp;
    if (fp.organization) params.organization = fp.organization;
    if (fp.organization_exclude) params.organization_exclude = fp.organization_exclude;
    if (orderIdFilter.trim()) params.order_id = orderIdFilter.trim();
    const resp = await axios.get(`${apiPrefix}/orders/by-group`, { params, timeout: FINANCE_TIMEOUT_MS });
    setExpandedOrders(prev => ({ ...prev, [k]: resp.data.rows || [] }));
  };

  const toggleOrder = async (dateKey, g, o) => {
    const orderKey = isWB ? (o.order_id || o.srid) : (isYandex ? o.orders_id : o.base_order);
    const opKey = `${dateKey}|${g.gruppa}|${g.podgruppa || ''}|${orderKey}`;
    if (expandedOps[opKey]) {
      setExpandedOps(prev => { const c = { ...prev }; delete c[opKey]; return c; });
      return;
    }
    let params, url;
    if (isWB) {
      params = o.shk_id ? { shk_id: o.shk_id } : { srid: o.srid };
      if (o.order_dt) params.order_dt = o.order_dt;
      url = '/analytics/wb/operations';
    } else if (isYandex) {
      params = { orders_id: o.orders_id };
      url = '/analytics/ya/operations';
    } else {
      params = withDateMode({ base_order: o.base_order, gruppa: g.gruppa || '', podgruppa: g.podgruppa || '' });
      url = '/analytics/operations';
    }
    const resp = await axios.get(url, { params, timeout: FINANCE_TIMEOUT_MS });
    setExpandedOps(prev => ({ ...prev, [opKey]: resp.data.rows || [] }));
  };

  // Render group row
  const renderGroupCells = (g) => {
    if (isWB) {
      return [
        g.gruppa, g.podgruppa,
        g.items_ordered, g.items_buyout, g.items_return, g.items_defect, g.items_kgt, g.buyout_rate_pct,
        g.retail_price_net_sum,
        g.commission_sum,
        g.commission_pct_net,
        g.acquiring_sum,
        g.retail_amount_net_sum,
        g.ppvz_for_pay_sum, g.logistics_sum, g.avg_logistics,
        g.storage_sum, g.ads_sum, g.other_sum,
        +(Number(g.ppvz_for_pay_sum || 0) + Number(g.logistics_sum || 0) + Number(g.ads_sum || 0) + Number(g.other_sum || 0) + Number(g.storage_sum || 0)).toFixed(2),
        g.avg_kgt_return_cost,
        g.nds_out_sum, g.nds_in_mp_sum, g.nds_in_prod_sum,
        g.nds_sum, g.cost_sum, g.profit, g.roi_pct
      ];
    }
    if (isYandex) {
      const gn = (k) => Number(g[k] || 0);
      const gCommNeg = -Math.abs(gn('commission_sum'));
      const gTotalDed = +(gCommNeg - gn('logistics_sum') + gn('acquiring_sum') + gn('ads_sum') + gn('ads_bonus_sum') + gn('storage_sum') + gn('other_sum')).toFixed(2);
      const gTotalToPay = +(gn('payment_sum') + gTotalDed).toFixed(2);
      return [g.gruppa, g.podgruppa, g.items_ordered, g.items_sold, g.items_returned, g.items_cancelled, g.retail_sum, g.payment_sum, gCommNeg, g.ppvz_for_pay_sum, g.bonus_accrual_sum, g.bonus_writeoff_sum, g.ads_bonus_sum, g.ads_sum, g.logistics_sum, g.acquiring_sum, g.storage_sum, g.other_sum, gTotalDed, gTotalToPay, g.nds_out_sum, g.nds_in_mp_sum, g.nds_in_prod_sum, g.nds5_sum, g.cost_sum, g.profit, g.roi_pct];
    }
    return [g.gruppa, g.podgruppa, g.items_ordered_total, g.items_cancelled_immediately, g.items_buyout, g.buyout_rate_pct, g.accruals_for_sale_sum, g.commission_sum, g.nds_out_sum, g.nds_in_mp_sum, g.nds_in_prod_sum, g.nds_sum, g.amount_sum, g.logistics_cost_sum, g.acquiring_cost_sum, g.other_cost_sum, g.cost_sum, g.profit, g.roi_pct];
  };

  // Order headers
  const getOrderHeaders = () => {
    if (isWB) return ['Заказ', 'Артикул', 'Заказано', 'Выкуплено', 'Возврат', 'Продажи (розн)', 'Коммиссия', 'Коммиссия %', 'Эквайринг', 'Продажи (СПП)', 'К перечисл.', 'Логистика', 'Хранение', 'Реклама', 'Прочие', 'Итого к оплате', 'НДС с продаж', 'НДС к выч. WB', 'НДС к выч. произв.', 'НДС к уплате', 'Себестоим.', 'Профит', 'ROI %'];
    if (isYandex) return ['Заказ', 'Дата доставки', 'SKU', 'Статус', 'Кол-во', 'По нашей цене', 'Сумма платежа', 'Комиссия', 'Доход - маркет', 'Бонусы начисл.', 'Бонус −', 'Рекл. бонусы', 'Реклама', 'Логистика', 'Эквайринг', 'Хранение', 'Прочие', 'Всего удержано', 'Итого к оплате', 'НДС с продаж', 'НДС к выч. ЯМ', 'НДС к выч. произв.', 'НДС к уплате', 'Себестоим.', 'Профит', 'ROI %'];
    return ['Заказ', 'Заказано', 'Отменено', 'Выкуплено', '% выкупа', 'Сумма заказов', 'Комиссии', 'НДС с продаж', 'НДС к выч. Ozon', 'НДС к выч. произв.', 'НДС к уплате', 'К перечисл.', 'Логистика', 'Эквайринг', 'Прочие', 'Себестоим.', 'Профит', 'ROI %'];
  };

  const renderOrderCells = (o) => {
    if (isWB) return [
      o.order_id || o.srid, o.sa_name,
      o.items_ordered, o.items_buyout, o.items_return,
      o.retail_price_net_sum,
      o.commission_sum,
      o.commission_pct_net,
      o.acquiring_sum,
      o.retail_amount_net_sum,
      o.ppvz_for_pay_sum, o.logistics_sum, o.storage_sum, o.ads_sum, o.other_sum,
      +(Number(o.ppvz_for_pay_sum || 0) + Number(o.logistics_sum || 0) + Number(o.ads_sum || 0) + Number(o.other_sum || 0) + Number(o.storage_sum || 0)).toFixed(2),
      o.nds_out_sum, o.nds_in_mp_sum, o.nds_in_prod_sum,
      o.nds_sum, o.cost_sum, o.profit, o.roi_pct
    ];
    if (isYandex) {
      const on = (k) => Number(o[k] || 0);
      const oCommNeg = -Math.abs(on('commission_sum'));
      const oTotalDed = +(oCommNeg - on('logistics_sum') + on('acquiring_sum') + on('ads_sum') + on('ads_bonus_sum') + on('storage_sum') + on('other_sum')).toFixed(2);
      const oTotalToPay = +(on('payment_sum') + oTotalDed).toFixed(2);
      return [o.orders_id, o.delivery_date || '-', o.sku, o.status, o.quantity, o.retail_sum, o.payment_sum, oCommNeg, o.ppvz_for_pay_sum, o.bonus_accrual, o.bonus_writeoff, o.ads_bonus_sum, o.ads_sum, o.logistics_sum, o.acquiring_sum, o.storage_sum, o.other_sum, oTotalDed, oTotalToPay, o.nds_out_sum, o.nds_in_mp_sum, o.nds_in_prod_sum, o.nds5_sum, o.cost_sum, o.profit, o.roi_pct];
    }
    return [o.base_order, o.items_ordered_total, o.items_cancelled_immediately, o.items_buyout, o.buyout_rate_pct, o.accruals_for_sale_sum, o.commission_sum, o.nds_out_sum, o.nds_in_mp_sum, o.nds_in_prod_sum, o.nds_sum, o.amount_sum, o.logistics_cost_sum, o.acquiring_cost_sum, o.other_cost_sum, o.cost_sum, o.profit, o.roi_pct];
  };

  // Ops headers
  const getOpsHeaders = () => {
    if (isWB) return ['Дата операции', 'Тип операции', 'Тип бонуса', 'Артикул', 'Кол-во', 'Цена', 'Сумма', 'К перечисл.', 'Логистика', 'Эквайринг', 'Хранение', 'Штраф', 'Склад'];
    if (isYandex) return ['SKU', 'Статус', 'Кол-во', 'По нашей цене', 'Сумма платежа', 'Комиссия', 'Доход - маркет', 'Бонусы начисл.', 'Бонус −', 'Рекл. бонусы', 'Реклама', 'Логистика', 'Эквайринг', 'Хранение', 'Прочие', 'Всего удержано', 'Итого к оплате', 'НДС с продаж', 'НДС к выч. ЯМ', 'НДС к выч. произв.', 'НДС к уплате', 'Себестоим.', 'Профит', 'ROI %'];
    return ['Дата операции', 'Тип операции', 'SKU', 'Товар', 'Кол-во', 'Сумма заказов', 'Комиссия', 'НДС к уплате', 'К перечисл.', 'Логистика', 'Эквайринг', 'Прочие', 'Себестоим.'];
  };

  const renderOpsCells = (op) => {
    if (isWB) return [op.operation_date, op.supplier_oper_name, op.bonus_type_name, op.sa_name, op.quantity, op.retail_price, op.retail_amount, op.ppvz_for_pay, op.delivery_rub, op.acquiring_fee, op.storage_fee, op.penalty, op.office_name];
    if (isYandex) {
      const opn = (k) => Number(op[k] || 0);
      const opCommNeg = -Math.abs(opn('commission'));
      const acqSum = -(opn('acquiring') + opn('acquiring2'));
      const storageNorm = -Math.abs(opn('storage_goods'));
      const adsNorm = -Math.abs(opn('sales_boost'));
      const adsBonusNorm = 0;
      const otherNorm = -Math.abs(opn('order_processing') + opn('warehouse_processing') + opn('loyalty_program') + opn('installment_plan') + opn('return_goods'));
      const logiSum = opn('logistic') + opn('mid_mile');
      const opTotalDed = +(opCommNeg - logiSum + acqSum + adsNorm + adsBonusNorm + storageNorm + otherNorm).toFixed(2);
      const opTotalToPay = +(opn('payment_sum_client') + opTotalDed).toFixed(2);
      const opNdsPay = op.nds_pay != null ? opn('nds_pay') : opn('nds5');
      return [op.sku, op.status, op.shipped_quantity, op.price_sales, op.payment_sum_client, opCommNeg, op.ppvz_for_pay, op.bonus_accrual, op.bonus_writeoff, adsBonusNorm, adsNorm, logiSum, acqSum, storageNorm, otherNorm, +opTotalDed.toFixed(2), opTotalToPay, op.nds_out, op.nds_in_mp, op.nds_in_prod, opNdsPay, op.cost, op.profit, op.roi_pct];
    }
    return [op.operation_date, op.operation_type_name, op.item_sku, op.item_name, op.quantity, op.accruals_for_sale, op.sale_commission, op.nds_pay ?? op.nds5, op.amount, op.logistics_cost, op.acquiring_cost, op.other_cost, op.cost_recorded];
  };

  const chartColors = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#d32f2f', '#0288d1', '#7b1fa2', '#5d4037', '#455a64', '#689f38'];

  const renderChartTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const item = payload[0]?.payload;
    if (!item) return null;
    return (
      <Paper sx={{ p: 1, border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>{item.label}</Typography>
        <Typography variant="caption" sx={{ display: 'block' }}>{fmtMoney(item.value)} ₽</Typography>
        <Typography variant="caption" color="text.secondary">{fmtPct(item.share_pct)}</Typography>
      </Paper>
    );
  };

  const renderBreakdownChart = (title, items, total, emptyText) => {
    const data = (items || []).filter((item) => Number(item.value || 0) > 0);
    return (
      <Paper sx={{ p: 2, height: 360 }}>
        <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Итого: {fmtMoney(total || 0)} ₽
        </Typography>
        {data.length ? (
          <Grid container spacing={1} sx={{ height: 285 }}>
            <Grid item xs={12} md={6} sx={{ minHeight: 230 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data}
                    dataKey="value"
                    nameKey="label"
                    innerRadius="55%"
                    outerRadius="85%"
                    paddingAngle={1}
                  >
                    {data.map((entry, index) => (
                      <Cell key={entry.key || `${entry.label}_${index}`} fill={chartColors[index % chartColors.length]} />
                    ))}
                  </Pie>
                  <ChartTooltip content={renderChartTooltip} />
                </PieChart>
              </ResponsiveContainer>
            </Grid>
            <Grid item xs={12} md={6} sx={{ maxHeight: 250, overflow: 'auto' }}>
              {data.map((item, index) => (
                <Box key={item.key || `${item.label}_${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: chartColors[index % chartColors.length], flex: '0 0 auto' }} />
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography variant="caption" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {item.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fmtMoney(item.value)} ₽ · {fmtPct(item.share_pct)}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Grid>
          </Grid>
        ) : (
          <Box sx={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'text.secondary' }}>
            {emptyText}
          </Box>
        )}
      </Paper>
    );
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Финансовая аналитика
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} sm={12} md={4}>
            <DateRangeField
              valueFrom={dateFrom}
              valueTo={dateTo}
              onChange={({ from, to }) => { setDateFrom(from); setDateTo(to); }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="№ заказа"
              value={orderIdFilter}
              onChange={e => setOrderIdFilter(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') load(); }}
              size="small"
              fullWidth
              placeholder="Поиск..."
            />
          </Grid>
        </Grid>
        <Box sx={{ mb: 2 }}>
          <ProductFilters value={filters} onChange={setFilters} showArticle={false} />
        </Box>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs="auto">
            <ToggleButtonGroup
              value={agg}
              exclusive
              onChange={(e, v) => { if (v) setAgg(v); }}
              size="small"
            >
              <ToggleButton value="day" sx={{ textTransform: 'none', px: 1.5 }}>День</ToggleButton>
              <ToggleButton value="week" sx={{ textTransform: 'none', px: 1.5 }}>Неделя</ToggleButton>
              <ToggleButton value="month" sx={{ textTransform: 'none', px: 1.5 }}>Месяц</ToggleButton>
              <ToggleButton value="quarter" sx={{ textTransform: 'none', px: 1.5 }}>Квартал</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs="auto">
            <ToggleButtonGroup
              value={dateMode}
              exclusive
              onChange={(e, v) => { if (v) setDateMode(v); }}
              size="small"
            >
              {[
                ['order', 'По дате заказа'],
                ['ops', opsModeLabel],
                ['cohort_ops', 'Заказы периода → по операциям'],
              ].map(([value, label]) => (
                <Tooltip key={value} title={dateModeHints[value]} arrow placement="top" enterDelay={300}>
                  <ToggleButton value={value} sx={{ textTransform: 'none', px: 1.5 }}>{label}</ToggleButton>
                </Tooltip>
              ))}
            </ToggleButtonGroup>
          </Grid>
          <Grid item xs />
          <Grid item xs={12} sm="auto">
            <Button
              variant="contained"
              onClick={load}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              {loading ? 'Загрузка...' : 'Загрузить'}
            </Button>
          </Grid>
        </Grid>
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
          {dateModeHints[dateMode]}
        </Typography>
      </Paper>

      {/* Zero-cost warning */}
      {zeroCostOrders.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Обнаружены заказы с нулевой себестоимостью ({zeroCostOrders.length}):
          </Typography>
          <Box sx={{ maxHeight: 160, overflow: 'auto', fontSize: '0.82rem' }}>
            {zeroCostOrders.map((o, i) => (
              <Box key={i} component="span">
                {i > 0 && '; '}
                <b>{o.order_id}</b> ({o.order_date}){o.sa_name ? ` — ${o.sa_name}` : ''}
              </Box>
            ))}
          </Box>
        </Alert>
      )}

      {breakdown && rows.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} lg={6}>
            {renderBreakdownChart(
              'Доходы',
              breakdown.income,
              breakdown.totals?.income,
              'Нет доходных операций за выбранный период'
            )}
          </Grid>
          <Grid item xs={12} lg={6}>
            {renderBreakdownChart(
              'Расходы по операциям',
              breakdown.expenses,
              breakdown.totals?.expenses,
              'Нет расходных операций за выбранный период'
            )}
          </Grid>
        </Grid>
      )}

      {breakdownLoading && rows.length > 0 && !breakdown && (
        <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Загружаем диаграммы...
          </Typography>
        </Paper>
      )}

      {/* Main Table */}
      <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 350px)' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={headerStyle} style={{ width: 40 }}></TableCell>
              {headers.map(h => {
                const tip = columnTooltips[h.key];
                return (
                  <TableCell key={h.key} sx={headerStyle} align={h.align}>
                    {tip ? (
                      <Tooltip title={tip} arrow placement="top" enterDelay={300}>
                        <span style={{ cursor: 'help', borderBottom: '1px dashed #999' }}>{h.label}</span>
                      </Tooltip>
                    ) : h.label}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow><TableCell colSpan={headers.length + 1} align="center" sx={{ py: 4 }}>{loading ? 'Загрузка...' : 'Нет данных. Выберите параметры и нажмите "Загрузить"'}</TableCell></TableRow>
            ) : (
              <>
                {rows.map((r, idx) => {
                  const canExpand = !isPeriodic;
                  const dateKey = canExpand ? r.order_date : `${r.week_start || ''}|${r.week_end || ''}|${r.mp ?? ''}|${r.organization || ''}`;
                  const isExp = canExpand ? !!expanded[dateKey] : false;
                  const list = canExpand ? (expanded[dateKey] || []) : [];
                  return (
                    <Fragment key={`${dateKey}_${idx}`}>
                      <TableRow
                        hover
                        sx={{ cursor: canExpand ? 'pointer' : 'default', bgcolor: isExp ? '#e3f2fd' : 'inherit' }}
                        onClick={canExpand ? (() => toggleDate(dateKey)) : undefined}
                      >
                        <TableCell sx={cellStyle}>
                          {canExpand ? (
                            <IconButton size="small">{isExp ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}</IconButton>
                          ) : null}
                        </TableCell>
                        {headers.map(h => (
                          <TableCell key={h.key} sx={cellStyle} align={h.align}>
                            {(() => {
                              const main = getCellValue(r, h.key);
                              if (!isPeriodic || h.key === 'order_date') return main;
                              const metricObj = r?.[h.key];
                              const shareNode = renderShare(metricObj, h.key);
                              if (!shareNode) return main;
                              const mainJustify =
                                h.align === 'right'
                                  ? 'flex-end'
                                  : (h.align === 'center' ? 'center' : 'flex-start');
                              return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', lineHeight: 1.15 }}>
                                  <Box sx={{ width: '100%', display: 'flex', justifyContent: mainJustify }}>
                                    <span>{main}</span>
                                  </Box>
                                  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                                    {shareNode}
                                  </Box>
                                </Box>
                              );
                            })()}
                          </TableCell>
                        ))}
                      </TableRow>
                      {canExpand ? (
                      <TableRow>
                        <TableCell colSpan={headers.length + 1} sx={{ p: 0, borderBottom: isExp ? 'none' : undefined }}>
                          <Collapse in={isExp} timeout="auto" unmountOnExit>
                            <Box sx={{ pl: 4, pr: 1, py: 1, bgcolor: '#fafafa' }}>
                              <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#1f3b7a', fontWeight: 500 }}>Группы по дате {formatDate(dateKey)}</Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>{getGroupHeaders().map((h, i) => (<TableCell key={i} sx={headerStyle}>{h}</TableCell>))}<TableCell sx={headerStyle} style={{ width: 40 }}></TableCell></TableRow>
                                </TableHead>
                                <TableBody>
                                  {list.map((g, i) => {
                                    const gKey = `${dateKey}|${g.gruppa}|${g.podgruppa || ''}`;
                                    const isGrpExp = !!expandedOrders[gKey];
                                    const orders = expandedOrders[gKey] || [];
                                    return (
                                      <Fragment key={i}>
                                        <TableRow hover sx={{ cursor: 'pointer', bgcolor: isGrpExp ? '#e8f5e9' : 'inherit' }} onClick={() => toggleGroup(dateKey, g)}>
                                          {renderGroupCells(g).map((v, j) => {
                                            // 0-1: текст (группа/подгруппа), остальное: числа
                                            if (j < 2) return <TableCell key={j} sx={cellStyle}>{v ?? '-'}</TableCell>;
                                            // % позиции зависят от маркетплейса
                                            const pctIdx = isWB ? [7, 20] : (isYandex ? [26] : [5, 18]);
                                            if (pctIdx.includes(j)) return <TableCell key={j} sx={cellStyle}>{fmtPct(v)}</TableCell>;
                                            // средние значения
                                            const avgIdx = isWB ? [13] : [];
                                            if (avgIdx.includes(j)) return <TableCell key={j} sx={cellStyle}>{fmtNum(v, 2)}</TableCell>;
                                            // остальное — целые/деньги: показываем с разделением тысяч
                                            return <TableCell key={j} sx={cellStyle}>{fmtNum(v, 0)}</TableCell>;
                                          })}
                                          <TableCell sx={cellStyle}><IconButton size="small">{isGrpExp ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}</IconButton></TableCell>
                                        </TableRow>
                                        <TableRow>
                                          <TableCell colSpan={getGroupHeaders().length + 1} sx={{ p: 0 }}>
                                            <Collapse in={isGrpExp} timeout="auto" unmountOnExit>
                                              <Box sx={{ pl: 4, pr: 1, py: 1, bgcolor: '#f5f5f5' }}>
                                                <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#1b5e20', fontWeight: 500 }}>Заказы ({g.gruppa || '—'}{g.podgruppa ? ` / ${g.podgruppa}` : ''})</Typography>
                                                <Table size="small">
                                                  <TableHead>
                                                    <TableRow>{getOrderHeaders().map((h, i) => (<TableCell key={i} sx={headerStyle}>{h}</TableCell>))}<TableCell sx={headerStyle} style={{ width: 40 }}></TableCell></TableRow>
                                                  </TableHead>
                                                  <TableBody>
                                                    {orders.map((o, j) => {
                                                      const orderKey = isWB ? (o.order_id || o.srid) : (isYandex ? o.orders_id : o.base_order);
                                                      const opKey = `${dateKey}|${g.gruppa}|${g.podgruppa || ''}|${orderKey}`;
                                                      const isOrdExp = !!expandedOps[opKey];
                                                      const ops = expandedOps[opKey] || [];
                                                      return (
                                                        <Fragment key={j}>
                                                          <TableRow hover sx={{ cursor: 'pointer', bgcolor: isOrdExp ? '#fff8e1' : 'inherit' }} onClick={() => toggleOrder(dateKey, g, o)}>
                                                            {renderOrderCells(o).map((v, k) => {
                                                              // текстовые префиксы в заказах
                                                              const textCount = isWB ? 2 : (isYandex ? 4 : 1);
                                                              if (k < textCount) return <TableCell key={k} sx={cellStyle}>{v ?? '-'}</TableCell>;
                                                              // % позиции
                                                              const pctIdx = isWB ? [15] : (isYandex ? [25] : [4, 17]);
                                                              if (pctIdx.includes(k)) return <TableCell key={k} sx={cellStyle}>{fmtPct(v)}</TableCell>;
                                                              // остальное — числа с разделением тысяч
                                                              return <TableCell key={k} sx={cellStyle}>{fmtNum(v, 0)}</TableCell>;
                                                            })}
                                                            <TableCell sx={cellStyle}><IconButton size="small">{isOrdExp ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}</IconButton></TableCell>
                                                          </TableRow>
                                                          <TableRow>
                                                            <TableCell colSpan={getOrderHeaders().length + 1} sx={{ p: 0 }}>
                                                              <Collapse in={isOrdExp} timeout="auto" unmountOnExit>
                                                                <Box sx={{ pl: 4, pr: 1, py: 1, bgcolor: '#eef2f7' }}>
                                                                  <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#8a4b00', fontWeight: 500 }}>{isYandex ? `Товары в заказе ${orderKey}` : `Операции по заказу ${orderKey}`}</Typography>
                                                                  <Table size="small">
                                                                    <TableHead>
                                                                      <TableRow>{getOpsHeaders().map((h, i) => (<TableCell key={i} sx={headerStyle}>{h}</TableCell>))}</TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                      {ops.map((op, k) => (
                                                                        <TableRow key={k}>
                                                                          {renderOpsCells(op).map((v, l) => {
                                                                            // текстовые префиксы в операциях
                                                                            const textCount = isWB ? 4 : (isYandex ? 2 : 4);
                                                                            if (l < textCount) return <TableCell key={l} sx={cellStyle}>{v ?? '-'}</TableCell>;
                                                                            // у WB последний столбец - склад (текст)
                                                                            if (isWB && l === 12) return <TableCell key={l} sx={cellStyle}>{v ?? '-'}</TableCell>;
                                                                            // у Yandex последний столбец - ROI% (процент)
                                                                            if (isYandex && l === 23) return <TableCell key={l} sx={cellStyle}>{fmtPct(v)}</TableCell>;
                                                                            return <TableCell key={l} sx={cellStyle}>{fmtNum(v, 0)}</TableCell>;
                                                                          })}
                                                                        </TableRow>
                                                                      ))}
                                                                    </TableBody>
                                                                  </Table>
                                                                </Box>
                                                              </Collapse>
                                                            </TableCell>
                                                          </TableRow>
                                                        </Fragment>
                                                      );
                                                    })}
                                                  </TableBody>
                                                </Table>
                                              </Box>
                                            </Collapse>
                                          </TableCell>
                                        </TableRow>
                                      </Fragment>
                                    );
                                  })}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                      ) : null}
                    </Fragment>
                  );
                })}
                {/* Итоговая строка */}
                <TableRow sx={{ bgcolor: '#e8eaf6' }}>
                  <TableCell sx={{ ...cellStyle, fontWeight: 'bold' }}></TableCell>
                  <TableCell sx={{ ...cellStyle, fontWeight: 'bold' }}>Итого</TableCell>
                  {headers.slice(1).map(h => (
                    <TableCell key={h.key} sx={{ ...cellStyle, fontWeight: 'bold' }} align={h.align}>
                      {formatValueByKey(h.key, getTotalValue(h.key))}
                    </TableCell>
                  ))}
                </TableRow>
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
